import { toItem, type VaultItem } from '../../library/vault/vaultData'
import { textToHtml } from '../ofText'
import { toPost, toUser, type OfUser, type Post } from '../posts'

/**
 * OnlyFans Home (onlyfans.com/ and /posts/create, captured live 2026-10-08:
 * docs/of_home_queue_profile_capture_2026-10-08/SPEC.md §1, §4) → what the
 * Home page shows, and the new-post body it sends.
 */

type Raw = Record<string, unknown>

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
const str = (v: unknown) => (typeof v === 'string' ? v : '')
const list = (v: unknown) => (Array.isArray(v) ? (v as Raw[]) : [])

// ── Composer ────────────────────────────────────────────────────────────────

export type When = { kind: 'now' } | { kind: 'schedule'; at: Date } | { kind: 'later' }

export interface PollDraft {
  kind: 'poll' | 'quiz'
  options: string[]
  /** Quiz only: the index of the right answer among the filled options. */
  correct: number | null
  /** How long voting stays open. */
  days: number
}

/** Vault media in the composer; `preview` marks free teaser cells on a paid post. */
export type MediaRef = Pick<VaultItem, 'id'> &
  Partial<VaultItem> & {
    preview?: boolean
    /** Still uploading from the device — not yet a vault id. */
    pending?: boolean
  }

export function readyMedia(media: MediaRef[]): MediaRef[] {
  return media.filter((m) => !m.pending && Number.isFinite(m.id) && m.id > 0)
}

export interface Draft {
  /** Edit-box text; sent as OnlyFans' `<p>` paragraphs. */
  text: string
  labelIds: number[]
  /** Vault media. */
  media: MediaRef[]
  /** USD; 0 = free. */
  price: number
  poll: PollDraft | null
  /** null = no limit. */
  expireDays: number | null
  /** Fundraising target, USD; null = none. */
  target: number | null
  /**
   * Preferred tip amounts for fundraising (USD), up to 4.
   * Sent as `fundRaisingTipsPresets`; empty slots are dropped.
   */
  tipPresets: number[]
  /** Tagged creators (user ids). */
  tagged: number[]
  when: When
}

export const EMPTY_DRAFT: Draft = {
  text: '',
  labelIds: [],
  media: [],
  price: 0,
  poll: null,
  expireDays: null,
  target: null,
  tipPresets: [],
  tagged: [],
  when: { kind: 'now' },
}

/** OnlyFans shows up to 4 tip options; default first slot is $5. */
export const TIP_PRESETS_MAX = 4
export const TIP_PRESET_DEFAULT = 5

/** OnlyFans' limits shown in its own dialogs. */
export const PRICE_MIN = 3
/** "Please select only 40 media per post" (live 2026-10-08). */
export const MEDIA_MAX = 40
export const TARGET_MIN = 10
export const EXPIRY_DAYS = [1, 3, 7, 30] as const
export const POLL_DAYS = 7

const filled = (options: string[]) => options.map((o) => o.trim()).filter(Boolean)

/** The options sent (blank ones dropped) and the quiz answer's index among them (-1 = none). */
function pollSent(p: PollDraft): { options: string[]; correct: number } {
  const kept = p.options.map((o, i) => ({ o: o.trim(), i })).filter((k) => k.o)
  return { options: kept.map((k) => k.o), correct: p.correct === null ? -1 : kept.findIndex((k) => k.i === p.correct) }
}
const cents = (n: number) => Math.round(n * 100) / 100

/** Filled tip presets for `POST posts` — positive, ≤ target, max 4; default $5 (clamped). */
export function tipPresetsOf(tips: number[], target: number): number[] {
  const kept = tips
    .map(cents)
    .filter((n) => Number.isFinite(n) && n > 0 && n <= target)
    .slice(0, TIP_PRESETS_MAX)
  if (kept.length) return kept
  return [cents(Math.min(TIP_PRESET_DEFAULT, target))]
}

export function hasContent(d: Draft): boolean {
  return d.text.trim() !== '' || readyMedia(d.media).length > 0 || (d.poll !== null && filled(d.poll.options).length > 0)
}

export function hasPendingUpload(d: Draft): boolean {
  return d.media.some((m) => m.pending)
}

export type DraftError = 'empty' | 'mediaMax' | 'priceMin' | 'priceNeedsMedia' | 'targetMin' | 'pollOptions' | 'quizAnswer' | 'expiry' | 'pastDate'

/** Why OnlyFans would refuse this draft (checked before sending), or null. */
export function draftError(d: Draft, now = new Date()): DraftError | null {
  if (hasPendingUpload(d)) return 'empty'
  if (!hasContent(d)) return 'empty'
  const media = readyMedia(d.media)
  if (d.media.length > MEDIA_MAX) return 'mediaMax'
  if (d.price > 0 && d.price < PRICE_MIN) return 'priceMin'
  if (d.price > 0 && media.length === 0) return 'priceNeedsMedia'
  if (d.target !== null && d.target < TARGET_MIN) return 'targetMin'
  if (d.poll) {
    const { options, correct } = pollSent(d.poll)
    if (options.length < 2) return 'pollOptions'
    if (d.poll.kind === 'quiz' && correct < 0) return 'quizAnswer'
  }
  if (d.expireDays !== null && !(Number.isInteger(d.expireDays) && d.expireDays > 0)) return 'expiry'
  if (d.when.kind === 'schedule' && d.when.at.getTime() <= now.getTime()) return 'pastDate'
  return null
}

/**
 * `POST posts` body, keys in the order the OnlyFans page sends them. Media
 * and price go together (bigdayz.api create_post); polls, expiry and
 * fundraising use the gateway's keys; the schedule keys are captured live.
 */
export function previewIdsOf(media: MediaRef[], price: number): number[] {
  const ready = readyMedia(media)
  if (price <= 0 || ready.length === 0) return []
  const marked = ready.filter((m) => m.preview).map((m) => m.id)
  return marked.length ? marked : [ready[0]!.id]
}

export function buildPostBody(d: Draft): Record<string, unknown> {
  const media = readyMedia(d.media)
  const body: Record<string, unknown> = {
    preview: previewIdsOf(d.media, d.price),
    rfTag: [...d.tagged],
    rfGuest: [],
    rfPartner: [],
    text: d.text.trim() ? textToHtml(d.text.replace(/\s+$/, '')) : '',
  }
  if (d.labelIds.length) body.labelIds = [...d.labelIds]
  if (media.length || d.price > 0) {
    body.mediaFiles = media.map((m) => m.id)
    body.price = cents(d.price)
  }
  if (d.poll) {
    const { options, correct } = pollSent(d.poll)
    body.votingType = d.poll.kind === 'quiz' ? 2 : 1
    body.votingOptions = options
    body.votingDue = d.poll.days
    if (d.poll.kind === 'quiz' && correct >= 0) body.votingCorrectIndex = correct
  }
  if (d.expireDays !== null) body.expireDays = d.expireDays
  if (d.target !== null) {
    const target = cents(d.target)
    body.fundRaisingTargetAmount = target
    body.fundRaisingTipsPresets = tipPresetsOf(d.tipPresets, target)
  }
  if (d.when.kind === 'schedule') {
    body.isScheduled = 1
    body.scheduledDate = d.when.at.toISOString()
  } else if (d.when.kind === 'later') {
    body.isScheduled = 1
    body.isPublishedWithPeriod = 1
  }
  return body
}

// Schedule dialog default.
/** The next whole five minutes, at least an hour ahead. */
export function defaultScheduleTime(now = new Date()): Date {
  const d = new Date(now.getTime() + 60 * 60 * 1000)
  d.setSeconds(0, 0)
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5)
  return d
}

// ── Feed ────────────────────────────────────────────────────────────────────

export interface FeedPost extends Post {
  /** 'post' for every captured feed item; anything else is an OnlyFans insert (banner). */
  responseType: string
}

export interface FeedPage {
  list: FeedPost[]
  hasMore: boolean
  /** beforePublishTime for the next (older) page. */
  next: string | null
  /** afterPublishTime for posts newer than this page. */
  head: string | null
}

export function toFeedPost(raw: Raw): FeedPost {
  return { ...toPost(raw), responseType: str(raw.responseType) || 'post' }
}

export function feedPage(payload: Raw | null | undefined): FeedPage {
  return {
    list: list(payload?.list).map(toFeedPost),
    hasMore: payload?.hasMore === true,
    next: str(payload?.tailMarker) || null,
    head: str(payload?.headMarker) || null,
  }
}

/**
 * OnlyFans' "Promotional posts" switch: creators mark paid promotion with an
 * #ad / #advertising hashtag (no flag on the post itself was captured).
 */
export function isPromo(p: Post): boolean {
  return /(^|[\s>])#(ad|ads|advertising|promo)(?![\p{L}\p{N}_])/iu.test(p.text)
}

export interface FeedSettings {
  hidePromo: boolean
  hideBanners: boolean
}

/** OnlyFans keeps these two in the browser only (localStorage.feedSettings); so does the console. */
export const FEED_SETTINGS_KEY = 'onlymind.ofHome.feedSettings'

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>

export function readFeedSettings(storage: Storage | undefined = globalThis.localStorage): FeedSettings {
  try {
    const raw = JSON.parse(storage?.getItem(FEED_SETTINGS_KEY) ?? '{}') as Raw
    return { hidePromo: raw.hidePromo === true, hideBanners: raw.hideBanners === true }
  } catch {
    return { hidePromo: false, hideBanners: false }
  }
}

export function writeFeedSettings(s: FeedSettings, storage: Storage | undefined = globalThis.localStorage): void {
  try {
    storage?.setItem(FEED_SETTINGS_KEY, JSON.stringify(s))
  } catch {
    /* private mode: the switch lasts this visit */
  }
}

export function hideByFeedSettings<T extends FeedPost>(posts: T[], s: FeedSettings): T[] {
  return posts.filter((p) => !(s.hidePromo && isPromo(p)) && !(s.hideBanners && p.responseType !== 'post'))
}

// ── Stories ─────────────────────────────────────────────────────────────────

export interface StoryCard {
  userId: number
  storyId: number
  watched: boolean
  user: OfUser
  /** Small preview (stories/items `files.preview.sources.w150`). */
  preview: string
}

export interface StoryRef {
  userId: number
  storyId: number
  isWatched: boolean
}

export function toStoryRefs(payload: unknown): StoryRef[] {
  return list(payload).map((r) => ({ userId: num(r.userId), storyId: num(r.storyId), isWatched: r.isWatched === true }))
}

function previewOf(story: Raw | undefined): string {
  const media = list(story?.media)[0]
  const files = (media?.files ?? {}) as Record<string, Raw | undefined>
  const sources = (files.preview?.sources ?? {}) as Raw
  return str(sources.w150) || str(files.preview?.url) || str(files.thumb?.url) || str(files.squarePreview?.url)
}

/** Cards in stories/map order, from the stories/items answer (keyed by user id). */
export function storyCards(refs: StoryRef[], items: Record<string, Raw | undefined>): StoryCard[] {
  return refs.flatMap((r) => {
    const item = items[String(r.userId)]
    if (!item) return []
    const user = toUser((item.user ?? { id: r.userId }) as Raw)
    return [{ userId: r.userId, storyId: r.storyId, watched: r.isWatched, user, preview: previewOf(item.story as Raw | undefined) }]
  })
}

/** Own stories (users/me/stories) or a creator's (users/<id>/stories) → viewer items. */
export function storyMedia(payload: unknown): VaultItem[] {
  return list(payload).flatMap((s) => list(s.media).map(toItem))
}

// ── Hints ───────────────────────────────────────────────────────────────────

export interface Hint {
  id: number
  html: string
  permanent: boolean
}

/** OnlyFans writes "10/07/2026"; its page shows "Oct 7" (the year only when not this year). */
export function hintText(html: string, locale?: string, now = new Date()): string {
  return html.replace(/\b(\d{2})\/(\d{2})\/(\d{4})\b/g, (all, m: string, d: string, y: string) => {
    const date = new Date(Number(y), Number(m) - 1, Number(d))
    if (Number.isNaN(date.getTime())) return all
    const sameYear = date.getFullYear() === now.getFullYear()
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })
  })
}

export function toHint(raw: Raw): Hint {
  return { id: num(raw.id), html: str(raw.text), permanent: raw.isPermanent === true }
}

// ── Scheduled events (right column) ─────────────────────────────────────────

export interface ScheduledEvent {
  id: number
  type: 'post' | 'chat' | 'stream'
  at: string
  mediaCount: number
  thumb: string
}

export function toEvent(raw: Raw): ScheduledEvent {
  const entity = (raw.entity ?? {}) as Raw
  const media = list(entity.media).map(toItem)
  const type = raw.type === 'chat' || raw.type === 'stream' ? raw.type : 'post'
  return { id: num(raw.id), type, at: str(raw.publishDateTime), mediaCount: num(entity.mediaCount) || media.length, thumb: media[0]?.thumb ?? '' }
}

export interface EventDay {
  /** Local yyyy-mm-dd. */
  day: string
  items: ScheduledEvent[]
}

export function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function groupEvents(events: ScheduledEvent[]): EventDay[] {
  const days: EventDay[] = []
  for (const e of events) {
    const day = localDay(new Date(e.at))
    const last = days[days.length - 1]
    if (last?.day === day) last.items.push(e)
    else days.push({ day, items: [e] })
  }
  return days
}

/** `schedules?limit=4&filter[publishDate]=<today>&filter[publishDateEnd]=<today+1y>&filter[timeZone]=<tz>` */
export function upcomingQuery(now = new Date(), timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  const yearOn = new Date(now)
  yearOn.setFullYear(now.getFullYear() + 1)
  return {
    limit: 4,
    'filter[publishDate]': localDay(now),
    'filter[publishDateEnd]': localDay(yearOn),
    'filter[timeZone]': timeZone,
  }
}

// ── Composer pickers ────────────────────────────────────────────────────────

export interface Label {
  id: number
  name: string
  posts: number
  previews: string[]
}

export function toLabel(raw: Raw): Label {
  return { id: num(raw.id), name: str(raw.name), posts: num(raw.postsCount), previews: list(raw.posts).map((p) => str(p.url)).filter(Boolean) }
}

/** posts/tagged-friend-users → the creators who can be tagged. */
export function toTaggable(payload: unknown): OfUser[] {
  const items = list((payload as Raw | null)?.items)
  return items.map((i) => {
    const u = (i.user ?? {}) as Raw
    return { ...toUser(u), verified: u.isIdentityVerified === true || u.isVerified === true }
  })
}

export interface Promotion {
  image: string
  url: string
}

export function toPromotions(payload: unknown): Promotion[] {
  return list(payload).map((p) => ({ image: str(p.imageSrc), url: str(p.url) })).filter((p) => p.image)
}

// ── The Account (users/me) ──────────────────────────────────────────────────

export interface Me extends OfUser {
  /** OnlyFans' "Hide all Locked Posts" (PATCH users/me {paidFeed}); true = locked posts hidden. */
  paidFeed: boolean | null
  /** USD a month; 0 = free; null = not reported. */
  subscribePrice: number | null
}

export function toMe(raw: Raw): Me {
  return {
    ...toUser(raw),
    paidFeed: typeof raw.paidFeed === 'boolean' ? raw.paidFeed : null,
    subscribePrice: typeof raw.subscribePrice === 'number' ? raw.subscribePrice : null,
  }
}
